import { TooltipWrapper } from "@src/components/deprecated/DeprecatedTooltip/TooltipWrapper";
import { TagsSelectPopup } from "@src/components/deprecated/TagsSelectPopup/TagsSelectPopup";
import { downloadFile } from "@src/utils/hooks/useDownloadFile";
import { getLabelForMultipleSelected } from "@utils/actionUtilities";
import { Tag } from "data-hooks/company/tags/tags.type";
import { formatTagName } from "data-hooks/company/tags/tags.utils";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { FileLabel } from "../../../../../components/deprecated/FilePopup/FileLabel";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { ReadAccessPopup } from "../../../../../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { DataTableRelativesCell } from "../../../../../components/deprecated/RelativesEditorPopup/DataTableRelativesCell";
import { RelativesEditorPopup } from "../../../../../components/deprecated/RelativesEditorPopup/RelativesEditorPopup";
import { RenameFilePopup } from "../../../../../components/deprecated/RenameFilePopup/RenameFilePopup";
import { StatusTag, StatusTagColor } from "../../../../../components/deprecated/StatusTag/StatusTag";
import { FileTypeImageView } from "../../../../../components/ui-components/FileTypeImageView/FileTypeImageView";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { FileUtilities } from "../../../../../utils/fileUtilities";
import { Utilities } from "../../../../../utils/utilities";
import { PropertyData } from "../../../Property/PropertyData";
import { Role } from "../../../User/Role";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";
import { PropertyFileData } from "../../PropertyFileData";

export class PropertyFileDataHelper extends DataObjectHelper<PropertyFileData> {
  id = () => "propertyFileData";

  allFields(): DataObjectField<any, PropertyFileData>[] {
    return [];
  }
  displayName = () => t("main.file");

  getAllDataTableDatas(documents: PropertyFileData[]): DataTableData[] {
    return documents.map((file, index) => {
      return {
        key: file.id,
        cells: {
          type: { element: <FileTypeImageView height="32px" fileDocument={file} />, rawString: file.type },
          tags: {
            element: (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                }}
              >
                {file.tags.map((tag) => (
                  <TooltipWrapper
                    key={tag.id}
                    trigger={<StatusTag color={tag.badgeColor as StatusTagColor} text={formatTagName(tag)} />}
                    title={tag.description}
                  />
                ))}
              </div>
            ),
            rawString: file.tags.map((tag) => formatTagName(tag)).join(", "),
          },
          relatives: { element: <DataTableRelativesCell relatives={file.relatives} />, rawString: file.relatives.map((r) => r.name).join(", ") },
          name: {
            element: <FileLabel dataCy={`file-label-${index}`} file={file} otherFiles={documents} />,
            rawString: file.name,
            rawData: file,
          },
          size: FileUtilities.getFileSizeString(file.size),
          visibility: Utilities.getReadAccessString(file.readAccess),
          uploadDate: file.createdBy?.date,
        },
      };
    });
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "type",
        title: "",
        width: "30px",
      },
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "tags",
        title: t("main.tags"),
      },
      {
        key: "size",
        title: t("main.fileSize_abb"),
      },
      { key: "uploadDate", title: t("main.uploadDate") },
      {
        key: "relatives",
        title: t("main.connections"),
      },
      { key: "visibility", title: t("main.visibleFor") },
    ];
  }

  override getActionsForData = (file: PropertyFileData, selectedFiles: PropertyFileData[] = []): DataObjectAction<PropertyFileData>[] => {
    let property: PropertyData | undefined;
    if (file.parent?.parent instanceof PropertyData) property = file.parent.parent;
    else if (file.parent instanceof PropertyData) property = file.parent;

    const isMultipleFilesSelected = selectedFiles.length > 1;

    // Intialize tags, roles and relatives with an empty array if it was selected multiple files
    const relatives = isMultipleFilesSelected ? [] : file.relatives;
    const tags = isMultipleFilesSelected ? [] : file.tags;
    // According to the logic roles should have required landlord role value
    const roles = isMultipleFilesSelected ? [Role.LANDLORD] : [...(file.readAccess.roles || [Role.LANDLORD])];

    // Define labels for tags, roles and relatives popups if it was selected multiple files
    const relativesLabel = isMultipleFilesSelected ? getLabelForMultipleSelected(t("main.connections").toLowerCase()) : "";
    const rolesLabel = isMultipleFilesSelected ? getLabelForMultipleSelected(t("main.visibilitySettings").toLowerCase()) : "";

    const actions: DataObjectAction<PropertyFileData>[] = [];
    actions.push({
      id: "download",
      title: t("main.download"),
      iconName: "file_download",
      performAction: () => {
        ToastUtilities.presentPromise(t("main.downloading"), downloadFile(file, file.parent?.id));
        return Promise.resolve();
      },
    });

    const link = file.getExternalLink();
    if (link) {
      actions.push({
        id: "openExternal",
        title: t("main.openExternalFileTitle", { name: link.name }),
        iconName: "open_in_new",
        maxSelected: 1,
        link: link.url,
      });
    }

    if (!file.archived) {
      actions.push({
        id: "archive",
        iconName: "archive",
        title: t("main.archivePropertyFile"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            if (dataObjects.length > 1) {
              PopupTemplates.presentVerifyAction(t("main.archivePropertyFiles") + "?", t("main.propertyFilesArchivationCaution"), () => {
                resolve({ dataObjects });
              });
            } else {
              PopupTemplates.presentVerifyAction(t("main.archivePropertyFile") + "?", t("main.propertyFileArchivationCaution"), () => {
                resolve({ dataObjects });
              });
            }
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(file.archive());
        },
      });
    } else {
      actions.push({
        id: "unarchive",
        iconName: "archive",
        title: t("main.unarchivePropertyFile"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            if (dataObjects.length > 1) {
              PopupTemplates.presentVerifyAction(t("main.unarchivePropertyFiles") + "?", t("main.propertyFilesDearchivationCaution"), () => {
                resolve({ dataObjects });
              });
            } else {
              PopupTemplates.presentVerifyAction(t("main.unarchivePropertyFile") + "?", t("main.propertyFileDearchivationCaution"), () => {
                resolve({ dataObjects });
              });
            }
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(file.archive());
        },
      });
    }

    const editRoles = [Role.LANDLORD, Role.ENTREPRENEUR];
    const canEdit =
      property?.members.getData(LocalData.currentUser.id)?.roles.some((role) => editRoles.includes(role)) ||
      file.createdBy?.user === LocalData.currentUser;
    if (canEdit) {
      actions.push({
        id: "changeReadAccess",
        iconName: "visibility",
        title: t("main.changeVisibility"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <ReadAccessPopup
                id={id}
                readAccess={{ ...file.readAccess, roles }}
                requiredAccess={{ roles: [Role.LANDLORD] }}
                onSelect={(access) => {
                  resolve({ dataObjects, data: access });
                }}
                context={{ propertyID: property?.id }}
                accessGroups={["roles", "contractorGroups", "propertyUnits"]}
                label={rolesLabel}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(t("main.savingData"), file.saveUpdate({ readAccess: Utilities.convertReadAccess(updateData) }));
        },
      });

      actions.push({
        id: "tag",
        iconName: "label",
        title: t("main.addTags"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <TagsSelectPopup
                id={id}
                propertyId={dataObjects[0].parent?.id}
                value={tags.map((tag) => tag.id)}
                onChange={(tags) => {
                  resolve({ dataObjects, data: tags });
                }}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(
            t("main.savingData"),
            file.saveUpdate({
              "tags.standard": (updateData as Tag[]).map((c) => c.id),
            })
          );
        },
      });

      actions.push({
        id: "addRelatives",
        iconName: "link",
        title: t("documentAI.connections.editConnections"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <RelativesEditorPopup
                id={id}
                property={property}
                relatives={relatives}
                didSelectRelatives={(relatives) => resolve({ dataObjects, data: relatives })}
                label={relativesLabel}
                connectFrom="file"
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentSavingToast(
            file.saveUpdate({
              relatives: updateData,
            })
          );
        },
      });

      actions.push({
        id: "rename",
        iconName: "edit",
        title: t("main.editFileName"),
        maxSelected: 1,
        requiredConsent: (dataObjects) => {
          return Promise.resolve({ dataObjects, data: dataObjects });
        },
        performAction: (updateData) => {
          const file = updateData[0] as PropertyFileData;
          return Popup.present((id) => (
            <RenameFilePopup
              id={id}
              defaultFileName={file.name}
              fileType={file.type}
              handleSave={(name) => ToastUtilities.presentSavingToast(file.rename(name))}
            />
          )) as never;
        },
      });

      actions.push({
        id: "delete",
        iconName: "delete",
        iconColor: "var(--main-red-700)",
        title: t("main.delete") + " " + t("main.file").toLowerCase(),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.name),
              () => {
                resolve({ dataObjects });
              },
              t("main.cannotBeUndone")
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentDeletingToast(file.deleteDocument());
        },
      });
    }

    return actions;
  };
}
